import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { BuildingsIcon, LinkIcon, PlusIcon, WarningIcon } from '@phosphor-icons/react';
import { ApiError, Button, Input, Label, StatusChip } from '@smena/ui';
import { createProperty, createPropertyFromUrl, fetchProperties } from './api';

/**
 * Заведение объектов прямо в мастере настройки.
 *
 * Раньше на этом шаге стояли две кнопки-ссылки: «Импорт из URL» уводила на `/bookings?view=list`,
 * «Список объектов» - на `/properties`. То есть мастер, обещающий провести настройку за 20 минут,
 * на третьем шаге выкидывал человека и терял: вернуться можно было только через баннер сверху.
 *
 * Два пути рядом, потому что оба нужны: по ссылке - когда квартира уже висит на площадке
 * (анкета заполняется сама), вручную - когда объявления нет или площадку не пускает антибот.
 */
export function PropertyOnboardingCard() {
  const qc = useQueryClient();
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [city, setCity] = useState('');
  const [manual, setManual] = useState(false);

  const q = useQuery({ queryKey: ['properties'], queryFn: fetchProperties });
  const items = q.data ?? [];

  function done(): void {
    void qc.invalidateQueries({ queryKey: ['properties'] });
    // Шаг закрывается сам по факту наличия объекта - оглавление должно позеленеть сразу.
    void qc.invalidateQueries({ queryKey: ['setup'] });
  }

  const fromUrlMut = useMutation({
    mutationFn: () => createPropertyFromUrl(url.trim()),
    onSuccess: (r) => {
      setUrl('');
      // Повторная привязка того же объявления - не ошибка и не успех «создан»: раньше здесь
      // молча появлялся объект-близнец, и человек видел одну квартиру дважды.
      if (r.alreadyExisted) {
        toast.info(
          r.sourceAttached
            ? `«${r.title}» уже заведён - добавили эту ссылку как ещё один адрес того же объявления`
            : `«${r.title}» уже заведён по этой ссылке - ничего не меняли`,
        );
      } else {
        toast.success(`Объект «${r.title}» создан`);
      }
      if (r.warnings.length) {
        toast.warning(`Часть полей объявление не отдало: ${r.warnings.length}. Проверьте анкету.`);
      }
      done();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось разобрать объявление'),
  });

  const manualMut = useMutation({
    mutationFn: () => createProperty({ title: title.trim(), city: city.trim() || null }),
    onSuccess: (p) => {
      setTitle('');
      setCity('');
      setManual(false);
      toast.success(`Объект «${p.title}» создан`);
      done();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось создать объект'),
  });

  const urlValid = /^https?:\/\//i.test(url.trim());

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <BuildingsIcon weight="bold" className="size-4 text-accent-fg" />
        <span>В аккаунте объектов:</span>
        <StatusChip tone={items.length ? 'green' : 'muted'}>{items.length}</StatusChip>
      </div>

      {items.length ? (
        <ul className="flex flex-col gap-1">
          {items.slice(0, 6).map((p) => (
            <li
              key={p.id}
              className="flex flex-wrap items-baseline gap-x-2 rounded-md border border-line bg-panel2/40 px-3 py-2 text-sm"
            >
              <span className="font-medium">{p.title}</span>
              {p.city ? <span className="text-xs text-muted">{p.city}</span> : null}
            </li>
          ))}
          {items.length > 6 ? (
            <li className="text-[11px] text-muted">и ещё {items.length - 6}</li>
          ) : null}
        </ul>
      ) : null}

      <form
        className="flex flex-col gap-2 rounded-md border border-line bg-panel2/40 p-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!urlValid || fromUrlMut.isPending) return;
          fromUrlMut.mutate();
        }}
      >
        <div className="flex items-center gap-2 text-sm font-semibold">
          <LinkIcon weight="bold" className="size-4 text-accent-fg" /> Из объявления
        </div>
        <Label htmlFor="prop-url">Ссылка на объявление</Label>
        <Input
          id="prop-url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://sutochno.ru/detail/…"
          autoComplete="off"
          spellCheck={false}
        />
        <p className="text-[11px] text-muted">
          Суточно, Островок, Яндекс Путешествия. Заголовок, адрес, фото, правила и цена
          заполнятся сами. Wi-Fi и коды замков объявление не знает - их добавите в анкете.
        </p>
        <div>
          <Button type="submit" size="sm" disabled={!urlValid || fromUrlMut.isPending}>
            <PlusIcon weight="bold" className="size-4" />
            {fromUrlMut.isPending ? 'Читаем объявление…' : 'Создать объект'}
          </Button>
        </div>
        {url.trim() && !urlValid ? (
          <span className="text-[11px] text-red">Ссылка должна начинаться с http:// или https://</span>
        ) : null}
        {fromUrlMut.isError ? (
          <div className="flex items-start gap-2 rounded-md border border-amber/40 bg-amber/10 p-2 text-[11px] text-amber">
            <WarningIcon weight="fill" className="mt-0.5 size-3.5 shrink-0" />
            <span>
              Если площадка требует браузер или показала капчу - заведите объект вручную ниже,
              ссылку можно привязать позже в карточке объекта.
            </span>
          </div>
        ) : null}
      </form>

      {manual ? (
        <form
          className="flex flex-col gap-2 rounded-md border border-line bg-panel2/40 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!title.trim() || manualMut.isPending) return;
            manualMut.mutate();
          }}
        >
          <div className="text-sm font-semibold">Вручную</div>
          <Label htmlFor="prop-title">Название</Label>
          <Input
            id="prop-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Студия на Мира"
          />
          <Label htmlFor="prop-city">Город</Label>
          <Input
            id="prop-city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Казань"
          />
          <div className="flex flex-wrap gap-2">
            <Button type="submit" size="sm" disabled={!title.trim() || manualMut.isPending}>
              {manualMut.isPending ? 'Создаём…' : 'Создать объект'}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setManual(false)}>
              Отмена
            </Button>
          </div>
        </form>
      ) : (
        <div>
          <Button variant="ghost" size="sm" onClick={() => setManual(true)}>
            Завести вручную, без объявления
          </Button>
        </div>
      )}
    </div>
  );
}
